import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnInit,
    Output
} from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { AccountType, DraftPhaseApiService, SubscriptionTypeEntity, UserApiService } from 'app/api';
import { RouteSegment } from 'app/enums/route-segments.enum';
import { setActiveLanguageCode } from 'app/store/language/language.actions';
import {
    selectActiveLanguageCode,
    selectAvailableLanguages
} from 'app/store/language/language.selectors';
import { selectUrl } from 'app/store/router/router.selector';
import { logout } from 'app/store/user/user.actions';
import * as UserSelectors from 'app/store/user/user.selectors';
import dayjs from 'dayjs';
import { combineLatest, filter, map, Observable, of, Subject, switchMap } from 'rxjs';
import { selectSelectedProject } from '../../../store/projects/projects.selectors';
import * as DraftPhaseDataSelectors from '../../../store/draft-phase-creation/draft-phase-creation.selectors';
import * as TrialDraftPhaseDataSelectors from '../../../store/trial-draft-phase-creation/trial-draft-phase-creation.selectors';
import { ActivatedRoute, NavigationEnd, Router, RouterEvent } from '@angular/router';
import { UserState } from '../../../store/user/user.state';
import { INavLinkOption } from '../../../models/ui/nav-link-option.model';
import { trigger, state, transition, animate, style } from '@angular/animations';
import { BreakpointService } from '../../../services/breakpoint.service';
import { ContactUsPopupComponent } from '../../../pages/contact-us-popup/contact-us-popup.component';
import { MatDialog } from '@angular/material/dialog';
import { catchError } from 'rxjs/operators';

export interface linkTreeInterface {
    text?: string;
    translations$?: Observable<string>;
    route: string;
}

@UntilDestroy()
@Component({
    selector: 'app-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: [
        trigger('toggleMenu', [
            state('hidden', style({ visibility: 'hidden', height: '0px', opacity: 0 })),
            state('visible', style({ height: '*', visibility: 'visible', opacity: 1 })),
            transition('hidden => visible', animate('100ms ease-out')),
            transition('visible => hidden', animate('200ms ease-in'))
        ])
    ]
})
export class HeaderComponent implements OnInit {
    @Input() mainMenuOptions!: INavLinkOption[];
    @Input() userMenuItems!: INavLinkOption[];

    @Input() public userType!: string;
    @Output() public readonly sideMenuToggle = new EventEmitter<boolean>();

    public mainMenuVisible: 'hidden' | 'visible' = 'hidden';
    public userMenuVisible: 'hidden' | 'visible' = 'hidden';

    public RouteSegment = RouteSegment;
    public AccountType = AccountType;

    public userAccountTypeIsUnlimited = false;
    public userAccountTypeIsPremium = false;
    public userAccountTypeIsTrial = false;
    public userAccountValidToDate?: string;

    public subscriptionType?: SubscriptionTypeEntity;

    public languageSelectOpenState = false;
    public menuOpen = false;

    public selectedLanguageTitleEnglish$!: Observable<string>;
    public selectedLanguageTitleHungarian$!: Observable<string>;

    public changingValue: Subject<boolean> = new Subject();

    public couponCode: string = '';
    public couponCodeMessage: string = '';
    public couponCodeSubmit: boolean = false;

    public setActiveLanguageCode = setActiveLanguageCode;
    public selectUserState = UserSelectors.selectUserState;
    public selectAvailableLanguages = selectAvailableLanguages;
    public activeLanguageCode$ = this.store.select(selectActiveLanguageCode);
    public linkTree$: Observable<linkTreeInterface[]> = combineLatest([
        this.store.select(selectUrl),
        // THE HTTP call is slow WE NEED TO PASS draft phase name here
        this.route.queryParams.pipe(map((queryParams) => queryParams['projectId'])),
        this.store.select(selectSelectedProject),
        this.store.select(DraftPhaseDataSelectors.selectDraftPhaseData),
        this.store.select(TrialDraftPhaseDataSelectors.selectDraftPhaseData)
    ]).pipe(
        switchMap(
            ([url, draftPhaseId, currentProject, currentDraftPhase, trialCurrentDraftPhase]) => {
                const draftPhase = !url.includes(RouteSegment.DraftPhaseCreation)
                    ? of(null)
                    : currentDraftPhase
                    ? of(currentDraftPhase)
                    : trialCurrentDraftPhase
                    ? of(trialCurrentDraftPhase)
                    : draftPhaseId
                    ? this.draftPhaseApiService.getDraftPhase({ id: draftPhaseId })
                    : of(null);

                return combineLatest([
                    of(url),
                    of(currentProject),
                    draftPhase,
                    this.breakpoint.downMd$
                ]);
            }
        ),
        map(([url, currentProject, currentDraftPhase, breakPointMd]) => {
            console.log({ url, currentProject, currentDraftPhase });
            const currentDraftPhaseName = currentDraftPhase?.name ?? '';
            let link: linkTreeInterface[] = [];
            if (url.includes(RouteSegment.DraftPhaseCreation)) {
                link = [
                    {
                        translations$: this.translocoService.selectTranslate('menu.projects'),
                        route: `/${RouteSegment.Dashboard}`
                    },
                    {
                        text: currentProject?.name,
                        route: `/${RouteSegment.Dashboard}/${RouteSegment.DraftPhase}`
                    },
                    {
                        translations$: this.translocoService.selectTranslate('menu.phases'),
                        route: `/${RouteSegment.Dashboard}/${RouteSegment.DraftPhase}`
                    },
                    {
                        text: currentDraftPhaseName,
                        translations$: !currentDraftPhaseName
                            ? this.translocoService.selectTranslate('menu.new-phases')
                            : undefined,
                        route: `./${RouteSegment.DraftPhaseCreation}/${RouteSegment.Questions}`
                    }
                ];
            } else if (url.includes(RouteSegment.DraftPhase)) {
                link = [
                    {
                        translations$: this.translocoService.selectTranslate('menu.projects'),
                        route: `/${RouteSegment.Dashboard}`
                    },
                    {
                        text: currentProject?.name,
                        route: `/${RouteSegment.Dashboard}/${RouteSegment.DraftPhase}`
                    },
                    {
                        translations$: this.translocoService.selectTranslate('menu.phases'),
                        route: `/${RouteSegment.Dashboard}/${RouteSegment.DraftPhase}`
                    }
                ];
            } else if (url.includes(RouteSegment.Dashboard)) {
                link = [
                    {
                        translations$: this.translocoService.selectTranslate('menu.projects'),
                        route: `/${RouteSegment.Dashboard}`
                    }
                ];
            } else if (url.includes(RouteSegment.MapView)) {
                link = [
                    {
                        translations$: this.translocoService.selectTranslate('menu.map-view'),
                        route: `/${RouteSegment.MapView}`
                    }
                ];
            } else if (url.includes(RouteSegment.SalesManagement)) {
                link = [
                    {
                        translations$:
                            this.translocoService.selectTranslate('menu.sales-management'),
                        route: `/${RouteSegment.SalesManagement}`
                    }
                ];
            } else if (url.includes(RouteSegment.UserManagement)) {
                link = [
                    {
                        translations$: this.translocoService.selectTranslate('menu.analytics'),
                        route: `/${RouteSegment.UserManagement}`
                    }
                ];
            } else if (url.includes(RouteSegment.ItemManagement)) {
                link = [
                    {
                        translations$:
                            this.translocoService.selectTranslate('menu.item-management'),
                        route: `/${RouteSegment.ItemManagement}`
                    }
                ];
            } else if (url.includes(RouteSegment.Profile)) {
                link = [
                    {
                        translations$: this.translocoService.selectTranslate('menu.profile'),
                        route: `/${RouteSegment.Profile}`
                    }
                ];
            } else if (url.includes(RouteSegment.CompareTables)) {
                link = [
                    {
                        translations$: this.translocoService.selectTranslate('menu.projects'),
                        route: `/${RouteSegment.Dashboard}`
                    },
                    {
                        translations$: this.translocoService.selectTranslate(
                            'projects.table.menu.compare'
                        ),
                        route: `/${RouteSegment.CompareTables}`
                    }
                ];
            } else if (url.includes(RouteSegment.ContactPage)) {
                link = [
                    {
                        translations$: this.translocoService.selectTranslate(
                            'contact-list.name-list-link'
                        ),
                        route: `/${RouteSegment.ContactPage}`
                    }
                ];
            }
            /*  else {
              return getDefaultTitle()
            } */

            if (breakPointMd) {
                // const parts = link.filter((l) => l.text);
                // if (parts.length < 2) {
                //     return [...parts, link[link.length - 1]];
                // } else {
                //     return parts;
                // }
                return [link.pop()!].filter(Boolean);
            }

            return link.filter(Boolean);
        })
    );

    constructor(
        private readonly translocoService: TranslocoService,
        readonly store: Store,
        private readonly cdr: ChangeDetectorRef,
        private readonly breakpoint: BreakpointService,
        private readonly draftPhaseApiService: DraftPhaseApiService,
        private readonly route: ActivatedRoute,
        private readonly router: Router,
        public readonly dialog: MatDialog,
        private readonly userApiService: UserApiService
    ) {}

    ngOnInit(): void {
        this.store
            .select(UserSelectors.selectUserState)
            .pipe(untilDestroyed(this))
            .subscribe((user) => {
                console.log('user sub', user.subscription);

                this.subscriptionType = user.subscription[0]?.subscriptionType;

                /* TODO too many varibles */
                if (user.accountType === AccountType.Unlimited) {
                    this.userAccountTypeIsUnlimited = true;
                }
                if (user.accountType === AccountType.Premium) {
                    this.userAccountTypeIsPremium = true;
                }
                if (user.accountType === AccountType.Trial) {
                    this.userAccountTypeIsTrial = true;
                }

                if (user.subscription[0] && user.subscription[0].validTo) {
                    this.userAccountValidToDate = dayjs(user.subscription[0].validTo).format(
                        'YYYY.MM.DD HH:mm'
                    );
                }

                this.cdr.markForCheck();
            });

        this.selectedLanguageTitleEnglish$ = this.translocoService.selectTranslate(
            'header.languages.english'
        );
        this.selectedLanguageTitleHungarian$ = this.translocoService.selectTranslate(
            'header.languages.hungarian'
        );

        this.router.events
            .pipe(
                untilDestroyed(this),
                filter((event: any) => event instanceof NavigationEnd)
            )
            .subscribe((event: RouterEvent) => {
                // console.log('Navigation ended:', event.urlAfterRedirects);
                // You can perform any actions here when navigation completes
                this.mainMenuVisible = 'hidden';
                this.userMenuVisible = 'hidden';
            });
    }

    onLogout(): void {
        this.store.dispatch(logout());
    }

    onClickShowPopup() {
        this.changingValue.next(true);
    }

    logoClick() {
        this.sideMenuToggle.next(true);
    }

    getUserProfileImage(user: UserState) {
        return user.profileImage ?? 'assets/imgs/user-profile.webp';
    }

    onMenuItemClick(linkOption: INavLinkOption | null) {
        this.mainMenuVisible = 'hidden';
        this.userMenuVisible = 'hidden';
        if (linkOption?.link === RouteSegment.ContactUsPopup) {
            this.openContactUsDialog();
        }
    }

    toggleMainMenu() {
        this.userMenuVisible = 'hidden';
        this.mainMenuVisible = this.mainMenuVisible === 'visible' ? 'hidden' : 'visible';
    }

    toggleUserMenu() {
        this.mainMenuVisible = 'hidden';
        this.userMenuVisible = this.userMenuVisible === 'visible' ? 'hidden' : 'visible';
    }

    openContactUsDialog() {
        this.dialog.open(ContactUsPopupComponent, {
            width: '450px'
        });
    }

    submitCouponCode(couponCode: string) {
        if (!couponCode.length) {
            return;
        }
        this.couponCodeSubmit = true;
        this.userApiService
            .useCoupon({ body: { code: couponCode } })
            .pipe(
                catchError((err) => {
                    console.error(err);
                    this.couponCodeSubmit = false;
                    this.couponCodeMessage =
                        err.error.message ?? JSON.parse(err.error)?.message ?? 'HIBA';
                    throw err;
                })
            )
            .subscribe((res) => {
                this.couponCodeSubmit = false;
                this.couponCode = '';
                // this.store.dispatch(logout());
                // this.dialog.open(ContactUsPopupComponent, {
                //     width: '450px'
                // });
                window.location.reload();
            });
    }
}
